import type { CollectionWagsListing } from "@/features/listings";
import type { SearchResultItem } from "@/features/discovery";
import type { FindFeedBundledResponse } from "../feed/find-feed";

export const STREAMED_FEED_ROW_COUNT = 2;

export function nonEmptyFeedRows<
    Row extends { dogListings: readonly object[] },
>(rows: Row[]) {
    return rows.filter((row) => row.dogListings.length > 0);
}

export function streamedFeedRows(feed: FindFeedBundledResponse | undefined) {
    return (
        feed &&
        nonEmptyFeedRows(feed.result.listingRows).slice(
            0,
            STREAMED_FEED_ROW_COUNT,
        )
    );
}

function resolveProjectedListing(
    projection: SearchResultItem,
    listing: CollectionWagsListing | undefined,
): SearchResultItem {
    if (!listing) return projection;
    return {
        ...projection,
        name: listing.name ?? projection.name,
        location: listing.location ?? projection.location,
        birthday: listing.birthday ?? projection.birthday,
        ageLabel: listing.ageLabel ?? projection.ageLabel,
        weightBucket: listing.weightBucket ?? projection.weightBucket,
        sex: listing.sex ?? projection.sex,
        imageUrls: listing.imageUrls.length
            ? listing.imageUrls
            : projection.imageUrls,
        availableAt: listing.availableAt ?? projection.availableAt,
        breeds: listing.breeds.length
            ? listing.breeds.map((breed) => ({
                  id: breed.slug || breed.id,
                  name: breed.name,
              }))
            : projection.breeds,
        distanceMiles: projection.distanceMiles,
        minPrice: listing.priceRange?.min ?? projection.minPrice,
        maxPrice: listing.priceRange?.max ?? projection.maxPrice,
        description: listing.description ?? projection.description,
    };
}

export function feedListingIds(
    feed: FindFeedBundledResponse["result"] | undefined,
) {
    return (
        feed?.listingRows.flatMap((row) =>
            row.dogListings.map((listing) => listing.id),
        ) ?? []
    );
}

export function projectFeedRows(
    feed: FindFeedBundledResponse["result"] | undefined,
    listings: CollectionWagsListing[],
) {
    const listingById = new Map(
        listings.map((listing) => [listing.id, listing] as const),
    );
    return (feed?.listingRows ?? []).map((row) => ({
        ...row,
        dogListings: row.dogListings.map((listing) =>
            resolveProjectedListing(listing, listingById.get(listing.id)),
        ),
    }));
}
